import { z } from 'zod';
import {
  DronePlatformCategorySchema,
  DroneSensorTypeSchema,
  type DroneDownlinkType,
  type DronePlatformCategory,
  type DroneSensorType,
} from './drone-taxonomy';
import type { OperatorPricingTier, RateCard } from './drone-pricing';
import type { DroneReadiness } from './drone-mission';
import type { Point } from './geojson';

/**
 * Drone D1 #320 — "request live eyes" → discovery / matching → fit-ranked options.
 *
 * The STATELESS matching contract: given an area-of-interest (the incident / clicked
 * location) and the capability needs, surface the drone assets (#317 `drone_assets`)
 * that can REACH the AO and COVER the required sensors, ranked by a fit score. The
 * query persists NOTHING — no mission, no ledger, no asset write (mission creation +
 * dispatch is #321; the operator portal is #318; the state machine is #319).
 *
 * ATTRIBUTES-ONLY GUARANTEE: a candidate carries only the matchable attributes a gov
 * COP operator needs to choose — capabilities, reachability, platform, the operator
 * org's display name, and (forward-compat) an auth-level SUMMARY. It NEVER carries raw
 * operator credentials / PII. `auth_level_summary` is null today (the operator profile
 * that carries an auth level is #318) and folds in when that lands.
 *
 * The fit score is `capabilityScore × reachabilityScore` (the prompt's "capability
 * match × reachability/distance"). `computeDroneFitScore` is the single, pure,
 * unit-tested definition shared by the route and its tests so the formula can never
 * drift between them. AVAILABILITY / ETA fold into the score once #318 lands the
 * availability fields on `drone_assets` (today `eta_minutes` is null + the score
 * rests on capability + reachability only).
 */

/** Clamp `n` into [lo, hi]. */
function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/** Round to `places` decimals (avoids float noise in the score + slack fields). */
function round(n: number, places: number): number {
  const f = 10 ** places;
  return Math.round(n * f) / f;
}

export interface DroneFitScore {
  /** Fraction of the required sensors the asset satisfies. 1 when none required.
   *  Post-filter this is 1 (the matcher only returns assets that cover ALL required
   *  sensors); kept explicit so the formula stays honest and is the seam for a future
   *  partial-match ranking mode. Range [0, 1]. */
  capabilityScore: number;
  /** How comfortably the asset reaches the AO: 1 at its home base, 0 at the edge of
   *  its service radius. `1 − distance/serviceRadius`, clamped [0, 1]. An asset with a
   *  larger radius (more slack) at the same distance scores higher. */
  reachabilityScore: number;
  /** `capabilityScore × reachabilityScore`, rounded to 4 decimals. Range [0, 1]. */
  fitScore: number;
}

/**
 * The fit score for one candidate. PURE — no DB, no clock — so it is unit-testable and
 * identical on the route and in tests.
 *   capabilityScore   = totalRequired === 0 ? 1 : matchedRequired / totalRequired   (clamped)
 *   reachabilityScore = serviceRadiusKm > 0 ? 1 − distanceKm / serviceRadiusKm : 0  (clamped)
 *   fitScore          = capabilityScore × reachabilityScore
 */
export function computeDroneFitScore(input: {
  matchedRequired: number;
  totalRequired: number;
  distanceKm: number;
  serviceRadiusKm: number;
}): DroneFitScore {
  const { matchedRequired, totalRequired, distanceKm, serviceRadiusKm } = input;
  const capabilityScore =
    totalRequired <= 0 ? 1 : clamp(matchedRequired / totalRequired, 0, 1);
  const reachabilityScore =
    serviceRadiusKm > 0 ? clamp(1 - distanceKm / serviceRadiusKm, 0, 1) : 0;
  return {
    capabilityScore: round(capabilityScore, 4),
    reachabilityScore: round(reachabilityScore, 4),
    fitScore: round(capabilityScore * reachabilityScore, 4),
  };
}

/**
 * Phase 4c — the derived ETA-to-on-station (minutes) for a candidate, the headline §3B.6
 * figure. PURE — no DB, no clock — so the SERVER matcher (drone-match.ts route) and the
 * CLIENT coverage panel (apps/web droneCoverage.ts `deriveReach`) share ONE definition and
 * can never drift, the same discipline as `computeDroneFitScore`.
 *
 *   eta = launch_delay_minutes + (distance_km / cruise_speed_kmh) × 60   (rounded to whole min)
 *
 * Returns null when telemetry is absent — no positive cruise speed OR no launch delay — the
 * backward-compatible "—" an asset with no response-time profile shows (replacing the old
 * hardcoded `eta_minutes: null`). `flight_endurance_min` is the EYES-ON / on-station window
 * (4b vocabulary), NOT part of arrival ETA, so it is deliberately absent from this formula.
 */
export function computeDroneEtaMinutes(input: {
  distanceKm: number;
  cruiseSpeedKmh: number | null;
  launchDelayMinutes: number | null;
}): number | null {
  const { distanceKm, cruiseSpeedKmh, launchDelayMinutes } = input;
  if (cruiseSpeedKmh == null || cruiseSpeedKmh <= 0 || launchDelayMinutes == null) return null;
  return Math.round((distanceKm / cruiseSpeedKmh) * 60 + launchDelayMinutes);
}

/** Result of {@link eyesOnEnduranceCheck} — whether a requested eyes-on window outruns the drone. */
export interface EyesOnEnduranceCheck {
  /** True ONLY when endurance is known (> 0) AND the requested eyes-on exceeds it. The single
   *  boolean the warning surfaces render from — false on absent/zero telemetry (no false alarm). */
  exceeds: boolean;
  /** The matched drone's flight endurance (min), echoed for the warning copy; null when the
   *  asset carries no (positive) endurance telemetry. */
  enduranceMin: number | null;
  /** `ceil(requestedEyesOnMin / enduranceMin)` — the honest "would need N sorties / a relay"
   *  count, populated ONLY when `exceeds` (so it is always ≥ 2, since requested > endurance);
   *  null otherwise. Invariant: `sortiesNeeded != null` ⟺ `exceeds`. */
  sortiesNeeded: number | null;
}

/**
 * Phase 4c.1 — honest "requested eyes-on exceeds the drone's endurance" detector (#345). PURE — no
 * DB, no clock — so every request/coverage surface that warns (apps/web RequestLiveEyes option chip
 * + DroneCoverageBody §3B.6 endurance field) shares ONE definition and can never drift, the same
 * discipline as `computeDroneEtaMinutes` / `computeDroneFitScore`.
 *
 * `flight_endurance_min` is the EYES-ON / on-station WATCH WINDOW (4b vocabulary, doctrine §3B.6),
 * NOT an arrival term. When a requestor asks for a longer eyes-on duration than the matched drone
 * can fly, the COP must say so HONESTLY (the §G gate — surface the shortfall + the relay/sortie
 * reality) instead of silently accepting an impossible watch window. This is that detector.
 *
 *   exceeds       = enduranceMin is known (> 0) AND requestedEyesOnMin > enduranceMin
 *   sortiesNeeded = exceeds ? ceil(requestedEyesOnMin / enduranceMin) : null   (≥ 2 when present)
 *
 * Returns a clean not-exceeds / null result when endurance telemetry is absent or non-positive, or
 * the requested duration is absent / non-positive — so a drone with no endurance spec, or a request
 * with no stated window, NEVER shows a false warning.
 */
export function eyesOnEnduranceCheck(
  requestedEyesOnMin: number | null | undefined,
  flightEnduranceMin: number | null | undefined,
): EyesOnEnduranceCheck {
  const enduranceMin =
    flightEnduranceMin != null && flightEnduranceMin > 0 ? flightEnduranceMin : null;
  const requested = requestedEyesOnMin != null && requestedEyesOnMin > 0 ? requestedEyesOnMin : null;
  if (enduranceMin == null || requested == null || requested <= enduranceMin) {
    return { exceeds: false, enduranceMin, sortiesNeeded: null };
  }
  return { exceeds: true, enduranceMin, sortiesNeeded: Math.ceil(requested / enduranceMin) };
}

/** Request body for `POST /api/drone-match`. */
export const DroneMatchRequestSchema = z.object({
  /** Area of interest — the incident / clicked location to find live eyes for. */
  ao: z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
  }),
  /** Sensor capabilities the mission requires. An asset must cover ALL of them to be a
   *  candidate. Empty ⇒ no capability constraint (rank reachable assets by reachability). */
  required_sensors: z.array(DroneSensorTypeSchema).max(16).optional().default([]),
  /** Optional platform preference. When provided it FILTERS to that category. */
  platform_category: DronePlatformCategorySchema.optional(),
  /** Optional eyes-on window the requestor wants (minutes). Feeds the per-candidate price
   *  ESTIMATE (hourly × minutes/60). NOT a filter + NOT a ranking input — purely informational
   *  for the estimate, so the fit-rank is unaffected. (Drone pricing #344.) */
  requested_on_station_minutes: z.coerce.number().int().positive().max(100000).nullable().optional(),
  /** Max ranked options to return (the fit-ranked list is sliced after scoring). */
  limit: z.coerce.number().int().positive().max(50).optional().default(20),
});

/** The parsed/validated request (defaults applied) — server-side shape. */
export type DroneMatchRequest = z.infer<typeof DroneMatchRequestSchema>;
/** The request as a CALLER provides it (defaults still optional) — client-side shape. */
export type DroneMatchRequestInput = z.input<typeof DroneMatchRequestSchema>;

/** Reachability summary for a candidate (distance / range slack; ETA pending #318). */
export interface DroneMatchReachability {
  /** Always true for a returned candidate (the matcher only returns reachable assets). */
  reachable: boolean;
  /** Straight-line distance home-base → AO (km, 1 dp). */
  distance_km: number;
  /** The asset's own service radius (km). */
  service_radius_km: number;
  /** `service_radius_km − distance_km`, floored at 0 (km, 1 dp) — the range to spare. */
  slack_km: number;
  /** Derived ETA-to-on-station (minutes) for THIS AO — launch + transit, via
   *  `computeDroneEtaMinutes` over the asset's Phase 4c telemetry. null when the asset has no
   *  response-time profile (no cruise speed / launch delay) — the honest "—". */
  eta_minutes: number | null;
}

/**
 * One fit-ranked candidate — MATCHABLE ATTRIBUTES ONLY (never operator credentials/PII).
 * `operator_name` is the operator org's public display name; `auth_level_summary` is a
 * coarse regulatory-tier label (null until #318 lands the operator profile).
 */
export interface DroneMatchCandidate {
  drone_asset_id: string;
  operator_agency_id: string;
  operator_name: string;
  /** Government / public-service operator ⇒ the asset is $0-rated in the marketplace
   *  ("no charge"); a commercial operator is priced (Wave-1 drone unify #343). Optional
   *  on the type only so existing candidate fixtures need no change — the matcher route
   *  ALWAYS populates it. */
  government_owned?: boolean;
  asset_name: string;
  tail_number: string | null;
  /** Null only on the (FK-guarded, lockstep-pinned) impossible parse-miss — surfaced
   *  as null rather than a fabricated specific category. */
  platform_category: DronePlatformCategory | null;
  downlink_type: DroneDownlinkType | null;
  /** The asset's full sensor loadout. */
  sensors: DroneSensorType[];
  /** The subset of `required_sensors` this asset satisfies (== required for a match). */
  matched_sensors: DroneSensorType[];
  /** Home-base point (for map context). */
  location: Point;
  /** 8-point compass bearing AO → home base, or '—' when undefined. */
  bearing: string;
  reachability: DroneMatchReachability;
  /** Coarse regulatory-authorization tier label; null until #318. */
  auth_level_summary: string | null;
  /** The operator's published RATE CARD (agency-level, #344). null for a government_owned
   *  operator (their fleet is $0) or one that has not set any rate. DISPLAY only. */
  rate_card: RateCard | null;
  /** The competitive PRICING TIER: 'gov' ($0) / 'at-cost' / 'market' — derived from the
   *  operator's markers (government_owned, operator_kind), never from price magnitude (#344). */
  pricing_tier: OperatorPricingTier;
  /** Computed price ESTIMATE for this candidate (CAD): government_owned ⇒ 0; rate not set or
   *  insufficient inputs ⇒ null; else mobilization + hourly × (requested_on_station_minutes/60)
   *  + per_km × transit_km. An ESTIMATE — the operator still stamps a final quoted_price at
   *  accept. Surfaced ALONGSIDE fit_score; price is sortable but NEVER auto-ranked (#344). */
  estimate_cad: number | null;
  /** Response-time telemetry (Phase 4c — drone_assets columns, migration 0103) feeding the
   *  §3B.6 four-field block + the derived ETA. cruise_speed_kmh + launch_delay_minutes drive
   *  `computeDroneEtaMinutes`; flight_endurance_min is the EYES-ON window (4b vocabulary);
   *  area_coverage_sqkm is the per-sortie footprint. Optional on the type so existing
   *  fixtures need no change — the matcher route ALWAYS populates them (null when absent). */
  cruise_speed_kmh?: number | null;
  launch_delay_minutes?: number | null;
  flight_endurance_min?: number | null;
  area_coverage_sqkm?: number | null;
  /** Marketplace readiness band (§3B.6) — 'committed' when the asset has an active mission,
   *  else its availability_status. Optional on the type (fixtures omit it); the matcher route
   *  ALWAYS populates it so the match list shows whether a drone is busy elsewhere. */
  readiness?: DroneReadiness;
  capability_score: number;
  reachability_score: number;
  fit_score: number;
  /** Illustrative (seed / pending-partnership) asset, not a live operational feed. */
  illustrative: boolean;
}

/** Response of `POST /api/drone-match`. */
export interface DroneMatchResponse {
  ao: { lat: number; lon: number };
  requested: {
    required_sensors: DroneSensorType[];
    platform_category: DronePlatformCategory | null;
  };
  /** Number of candidates returned (after the fit-rank slice). */
  count: number;
  candidates: DroneMatchCandidate[];
}
